<template>
  <div>
    <div class="container-fluid">
      <div class="page-header">
        <Breadcrumbs main="account" title="profile" />
      </div>
      <div class="row">
        <!-- Profil : nom affiché, e-mail, photo -->
        <div class="col-lg-6">
          <div class="card">
            <div class="card-header">
              <h5>My profile</h5>
            </div>
            <div class="card-body">
              <div v-if="superadmin" class="alert alert-info">
                You are signed in as the super-admin. The profile is managed
                through the <code>ADMIN_PASSWORD</code> variable of the API.
              </div>
              <form v-else @submit.prevent="saveProfile">
                <div class="form-group">
                  <label>E-mail</label>
                  <input type="email" class="form-control" :value="user.email" disabled />
                  <small class="form-text text-muted"
                    >The e-mail address cannot be changed.</small
                  >
                </div>
                <div class="form-group">
                  <label>Display name</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="displayName"
                    maxlength="255"
                  />
                </div>
                <div class="form-group">
                  <label>Photo URL</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="photoURL"
                    maxlength="512"
                    placeholder="https://..."
                  />
                </div>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                    {{ saving ? 'Saving...' : 'Save changes' }}
                </button>
              </form>
            </div>
          </div>
        </div>

        <!-- Changement de mot de passe -->
        <div class="col-lg-6">
          <div class="card">
            <div class="card-header">
              <h5>Change password</h5>
            </div>
            <div class="card-body">
              <form v-if="!superadmin" @submit.prevent="changePassword">
                <div class="form-group">
                  <label>Current password</label>
                  <input
                    type="password"
                    class="form-control"
                    v-model="currentPassword"
                    autocomplete="current-password"
                  />
                </div>
                <div class="form-group">
                  <label>New password</label>
                  <input
                    type="password"
                    class="form-control"
                    v-model="newPassword"
                    autocomplete="new-password"
                  />
                  <small class="form-text text-muted">At least 8 characters.</small>
                </div>
                <div class="form-group">
                  <label>Confirm new password</label>
                  <input
                    type="password"
                    class="form-control"
                    v-model="confirmPassword"
                    autocomplete="new-password"
                  />
                </div>
                <button type="submit" class="btn btn-primary" :disabled="changingPwd">
                    {{ changingPwd ? 'Updating...' : 'Update password' }}
                </button>
              </form>
              <div v-else class="alert alert-info mb-0">
                Not available for the super-admin.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- Boutiques possédées -->
        <div class="col-lg-6">
          <div class="card">
            <div class="card-header">
              <h5>My stores</h5>
            </div>
            <div class="card-body">
              <ul class="list-group" v-if="sites.length">
                <li
                  class="list-group-item d-flex justify-content-between align-items-center"
                  v-for="host in sites"
                  :key="host"
                >
                  <span>{{ host }}</span>
                  <button
                    class="btn btn-sm btn-outline-primary"
                    @click="openStore(host)"
                  >
                    Open
                  </button>
                </li>
              </ul>
              <p v-else class="text-muted mb-0">No store yet.</p>
            </div>
          </div>
        </div>

        <!-- Abonnement (résumé) -->
        <div class="col-lg-6">
          <div class="card">
            <div class="card-header">
              <h5>Subscription</h5>
            </div>
            <div class="card-body">
              <div v-if="entitlement && entitlement.superadmin" class="alert alert-secondary mb-2">
                Super-admin: unlimited access.
              </div>
              <div v-else-if="subscription">
                <p class="mb-1">
                  Plan:
                  <strong>{{ planName || subscription.planId || '—' }}</strong>
                </p>
                <p class="mb-1">
                  Status:
                  <span :class="statusClass">{{ subscription.status }}</span>
                </p>
                <p class="mb-1" v-if="entitlement">
                  Stores: {{ entitlement.storeCount }}
                  /
                  {{ entitlement.maxStores === -1 ? 'unlimited' : entitlement.maxStores }}
                </p>
              </div>
              <p v-else class="text-muted mb-2">No subscription yet.</p>
              <router-link to="/account/subscription" class="btn btn-primary">
                Manage subscription
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "@/utils/firestore-shim";
import UserAuth from "../../auth/index.js";
import { setSelectedHost } from "../../utils/store-stats";

export default {
  name: "AccountProfile",
  data() {
    return {
      user: {},
      sites: [],
      subscription: null,
      entitlement: null,
      displayName: "",
      photoURL: "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      saving: false,
      changingPwd: false,
      superadmin: false
    };
  },
  computed: {
    planName() {
      return (this.entitlement && this.entitlement.planName) || "";
    },
    statusClass() {
      const s = this.subscription ? this.subscription.status : "";
      if (s === "active" || s === "trialing") return "text-success font-weight-bold";
      if (s === "past_due") return "text-warning font-weight-bold";
      if (s === "canceled") return "text-danger font-weight-bold";
      return "text-muted font-weight-bold";
    }
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async load() {
      try {
        const data = await firebase.auth().fetchProfile();
        UserAuth.setProfile(data);
        this.user = data.user || {};
        this.sites = data.sites || [];
        this.subscription = data.subscription || null;
        this.entitlement = data.entitlement || null;
        this.superadmin = Boolean(this.entitlement && this.entitlement.superadmin);
        this.displayName = this.user.displayName || "";
        this.photoURL = this.user.photoURL || "";
      } catch (e) {
        this.toast("Could not load the profile: " + e.message, "error");
      }
    },
    async saveProfile() {
      this.saving = true;
      try {
        const res = await firebase.api.patch("/auth/profile", {
          displayName: this.displayName,
          photoURL: this.photoURL
        });
        if (res && res.user) {
          this.user = res.user;
          await this.load();
        }
        this.toast("Profile updated.", "success");
      } catch (e) {
        this.toast(e.message, "error");
      } finally {
        this.saving = false;
      }
    },
    async changePassword() {
      if (this.newPassword !== this.confirmPassword) {
        this.toast("The new passwords do not match.", "error");
        return;
      }
      this.changingPwd = true;
      try {
        await firebase.api.post("/auth/change-password", {
          currentPassword: this.currentPassword,
          newPassword: this.newPassword
        });
        this.currentPassword = "";
        this.newPassword = "";
        this.confirmPassword = "";
        this.toast("Password updated.", "success");
      } catch (e) {
        this.toast(e.message, "error");
      } finally {
        this.changingPwd = false;
      }
    },
    openStore(host) {
      setSelectedHost(host);
      this.$router.push("/dashboard/site").catch(() => {});
    },
    toast(message, type) {
      if (this.$toasted) {
        this.$toasted.show(message, {
          theme: "bubble",
          position: "bottom-right",
          type: type || "info",
          duration: 3000
        });
      }
    }
  }
};
</script>
